import React, { useState, useEffect } from 'react';
import { ArrowLeft, DollarSign, Clock, Zap, Target, BarChart3, Users, TrendingUp, Settings } from 'lucide-react';

interface ROIPageProps {
  onBack: () => void;
  incidents: any[];
}

const ROIPage: React.FC<ROIPageProps> = ({ onBack, incidents }) => {
  const [analystRate, setAnalystRate] = useState(() => {
    const saved = localStorage.getItem('arbitium-roi-analystRate');
    return saved ? Number(saved) : 75;
  });
  const [manualTime, setManualTime] = useState(() => {
    const saved = localStorage.getItem('arbitium-roi-manualTime');
    return saved ? Number(saved) : 45;
  });
  const [annualSalary, setAnnualSalary] = useState(() => {
    const saved = localStorage.getItem('arbitium-roi-annualSalary');
    return saved ? Number(saved) : 95000;
  });
  const [showSettings, setShowSettings] = useState(false);

  // Persist settings to localStorage
  useEffect(() => {
    localStorage.setItem('arbitium-roi-analystRate', analystRate.toString());
  }, [analystRate]);

  useEffect(() => {
    localStorage.setItem('arbitium-roi-manualTime', manualTime.toString());
  }, [manualTime]);

  useEffect(() => {
    localStorage.setItem('arbitium-roi-annualSalary', annualSalary.toString());
  }, [annualSalary]);

  // Calculate metrics
  const remediatedCount = incidents.filter(i => i.status === 'remediated').length;
  const totalCount = incidents.length;
  const criticalCount = incidents.filter(i => i.severity === 'critical').length;
  const highCount = incidents.filter(i => i.severity === 'high').length;
  const mediumCount = incidents.filter(i => i.severity === 'medium').length;
  const lowCount = incidents.filter(i => i.severity === 'low').length;

  // Time saved (in hours)
  const timeSavedHours = (remediatedCount * manualTime) / 60;
  
  // Cost savings
  const costSavings = timeSavedHours * analystRate;
  
  // Efficiency gain (manual time vs automated ~1.2 seconds)
  const autoTimeSeconds = 1.2;
  const manualTimeSeconds = manualTime * 60;
  const efficiencyMultiplier = Math.round(manualTimeSeconds / autoTimeSeconds);
  
  // MTTR (Mean Time To Remediate) - using 1.2 seconds as our automated time
  const mttrSeconds = 1.2;
  
  // Analyst capacity (FTE equivalent)
  const fteEquivalent = timeSavedHours / 40; // 40 hours per week
  
  // Annual projections
  const monthlyRate = costSavings;
  const annualSavings = monthlyRate * 12;
  const roiPercentage = Math.round((annualSavings / annualSalary) * 100);

  const MetricCard: React.FC<{
    icon: React.ReactNode;
    title: string;
    value: string;
    formula: string[];
    color: string;
    extra?: React.ReactNode;
  }> = ({ icon, title, value, formula, color, extra }) => (
    <div className="bg-arbitium-dark/50 backdrop-blur-sm rounded-xl p-6 border border-arbitium-primary/20">
      <div className="flex items-center gap-3 mb-4">
        <div className={`p-2 rounded-lg ${color}`}>
          {icon}
        </div>
        <h3 className="text-gray-400 font-medium">{title}</h3>
      </div>
      <div className="text-3xl font-bold text-white mb-4">{value}</div>
      <div className="border-t border-arbitium-primary/10 pt-4">
        <p className="text-xs text-gray-500 mb-1">Formula:</p>
        {formula.map((line, i) => (
          <p key={i} className="text-sm text-gray-400 font-mono">{line}</p>
        ))}
        {extra}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="p-2 text-gray-400 hover:text-white hover:bg-arbitium-primary/10 rounded-xl transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <span>💰</span> ROI Dashboard
          </h1>
          <p className="text-gray-400 mt-1">Real-time value metrics from Arbitium automation</p>
        </div>
      </div>

      {/* Main Metrics Grid */}
      <div className="grid grid-cols-3 gap-6">
        <MetricCard
          icon={<DollarSign className="w-5 h-5 text-green-400" />}
          title="💵 COST SAVINGS"
          value={`$${costSavings.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
          formula={[
            `${remediatedCount} threats ×`,
            `${manualTime} min × $${analystRate}/hr`,
            `= $${costSavings.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          ]}
          color="bg-green-500/20"
        />

        <MetricCard
          icon={<Clock className="w-5 h-5 text-blue-400" />}
          title="⏱️ TIME SAVED"
          value={`${timeSavedHours.toFixed(1)} hours`}
          formula={[
            `${remediatedCount} threats ×`,
            `${manualTime} min avg manual time`,
            `= ${(remediatedCount * manualTime)} min = ${timeSavedHours.toFixed(1)} hrs`
          ]}
          color="bg-blue-500/20"
        />

        <MetricCard
          icon={<Zap className="w-5 h-5 text-yellow-400" />}
          title="⚡ EFFICIENCY GAIN"
          value={`${efficiencyMultiplier}x faster`}
          formula={[
            `${manualTime} min manual ÷`,
            `${autoTimeSeconds} sec automated`,
            `= ${efficiencyMultiplier}x improvement`
          ]}
          color="bg-yellow-500/20"
        />

        <MetricCard
          icon={<Target className="w-5 h-5 text-purple-400" />}
          title="🎯 MTTR"
          value={`${mttrSeconds} seconds`}
          formula={[
            `Avg(detection → remediation)`,
            ``,
            `Industry avg: 4.2 hours`
          ]}
          color="bg-purple-500/20"
          extra={
            <div className="mt-2 text-xs text-green-400">
              ✓ {Math.round((4.2 * 3600) / mttrSeconds)}x faster than industry
            </div>
          }
        />

        <MetricCard
          icon={<BarChart3 className="w-5 h-5 text-cyan-400" />}
          title="📊 THREATS HANDLED"
          value={`${totalCount} total`}
          formula={[
            `Breakdown:`
          ]}
          color="bg-cyan-500/20"
          extra={
            <div className="mt-2 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-red-400">🔴 Critical:</span>
                <span className="text-white">{criticalCount}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-orange-400">🟠 High:</span>
                <span className="text-white">{highCount}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-yellow-400">🟡 Medium:</span>
                <span className="text-white">{mediumCount}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-green-400">🟢 Low:</span>
                <span className="text-white">{lowCount}</span>
              </div>
            </div>
          }
        />

        <MetricCard
          icon={<Users className="w-5 h-5 text-pink-400" />}
          title="👤 ANALYST CAPACITY"
          value={`+${fteEquivalent.toFixed(2)} FTE`}
          formula={[
            `${timeSavedHours.toFixed(1)} hrs saved ÷`,
            `40 hrs/week`,
            `= ${fteEquivalent.toFixed(2)} FTE equivalent`
          ]}
          color="bg-pink-500/20"
        />
      </div>

      {/* Projected Annual Savings */}
      <div className="bg-gradient-to-r from-arbitium-primary/10 to-arbitium-accent/10 rounded-xl p-6 border border-arbitium-primary/20">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-arbitium-accent/20 rounded-lg">
            <TrendingUp className="w-5 h-5 text-arbitium-accent" />
          </div>
          <h3 className="text-lg font-semibold text-white">📈 PROJECTED ANNUAL SAVINGS</h3>
        </div>
        
        <div className="grid grid-cols-2 gap-8">
          <div>
            <div className="text-gray-400 mb-2">Monthly → Annual Projection</div>
            <div className="text-2xl font-bold text-white">
              ${monthlyRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/mo → 
              <span className="text-green-400"> ${annualSavings.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/yr</span>
            </div>
            <div className="text-sm text-gray-500 mt-2 font-mono">
              Formula: Current savings × 12 months
            </div>
          </div>
          <div>
            <div className="text-gray-400 mb-2">ROI vs 1 SOC Analyst (${annualSalary.toLocaleString()}/yr)</div>
            <div className="text-2xl font-bold text-green-400">
              {roiPercentage}% return
            </div>
            <div className="text-sm text-gray-500 mt-2 font-mono">
              Formula: (${annualSavings.toLocaleString()} ÷ ${annualSalary.toLocaleString()}) × 100
            </div>
          </div>
        </div>
      </div>

      {/* Assumptions */}
      <div className="bg-arbitium-dark/50 backdrop-blur-sm rounded-xl p-6 border border-arbitium-primary/20">
        <button 
          onClick={() => setShowSettings(!showSettings)}
          className="flex items-center gap-3 w-full text-left"
        >
          <div className="p-2 bg-gray-500/20 rounded-lg">
            <Settings className="w-5 h-5 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-white flex-1">⚙️ ASSUMPTIONS</h3>
          <span className="text-sm text-arbitium-accent">{showSettings ? 'Click to collapse' : 'Click to edit'}</span>
        </button>
        
        {showSettings && (
          <div className="grid grid-cols-3 gap-6 mt-6 pt-6 border-t border-arbitium-primary/10">
            <div>
              <label className="text-sm text-gray-400 block mb-2">Analyst Hourly Rate</label>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">$</span>
                <input
                  type="number"
                  value={analystRate}
                  onChange={(e) => setAnalystRate(Number(e.target.value))}
                  className="bg-arbitium-darker border border-arbitium-primary/30 rounded-lg px-3 py-2 text-white w-24 focus:outline-none focus:border-arbitium-primary"
                />
                <span className="text-gray-500">/hr</span>
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-400 block mb-2">Manual Time per Threat</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={manualTime}
                  onChange={(e) => setManualTime(Number(e.target.value))}
                  className="bg-arbitium-darker border border-arbitium-primary/30 rounded-lg px-3 py-2 text-white w-24 focus:outline-none focus:border-arbitium-primary"
                />
                <span className="text-gray-500">minutes</span>
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-400 block mb-2">SOC Analyst Annual Salary</label>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">$</span>
                <input
                  type="number"
                  value={annualSalary}
                  onChange={(e) => setAnnualSalary(Number(e.target.value))}
                  className="bg-arbitium-darker border border-arbitium-primary/30 rounded-lg px-3 py-2 text-white w-32 focus:outline-none focus:border-arbitium-primary"
                />
                <span className="text-gray-500">/yr</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ROIPage;
